import { getDefaultStore } from 'jotai'
import { atomWithStorage, createJSONStorage } from 'jotai/utils'
import {
  parseSlashCommandDefinition,
  type TSlashCommandDefinition
} from '@/lib/slash-command-definition'

/**
 * An installed slash command: a parsed version of a kind 31992 event, saved
 * (together with the author pubkey) when the user installs it. This is a local
 * cache of the user's kind 10992 list, see slash-commands-list.ts.
 */
export type TInstalledSlashCommand = TSlashCommandDefinition & {
  pubkey: string
  command: string
  /** created_at of the installed definition, used to pick up newer versions */
  created_at: number
}

/**
 * Older entries stored scopes as an object ({ kind, relay, author,
 * tagPresence, tagValue }) and had no ignores nor created_at.
 */
function normalizeInstalledSlashCommand(stored: any): TInstalledSlashCommand {
  let scopes: string[] = []
  if (Array.isArray(stored.scopes)) {
    scopes = stored.scopes
  } else if (stored.scopes) {
    const s = stored.scopes
    scopes = [
      ...(s.kind ?? []).map((kind: number) => `kind:${kind}`),
      ...(s.relay ?? []).map((relay: string) => `relay:${relay}`),
      ...(s.author ?? []).map((author: string) => `author:${author}`),
      ...(s.tagPresence ?? []).map((tagName: string) => `tag:${tagName}`),
      ...Object.entries((s.tagValue ?? {}) as Record<string, string[]>).flatMap(
        ([tagName, values]) => values.map((value) => `tag:${tagName}:${value}`)
      )
    ]
  }

  return {
    ...stored,
    args: stored.args ?? [],
    scopes,
    ignores: stored.ignores ?? [],
    created_at: stored.created_at ?? 0
  }
}

const jsonStorage = createJSONStorage<TInstalledSlashCommand[]>(() => localStorage)

export const installedSlashCommandsAtom = atomWithStorage<TInstalledSlashCommand[]>(
  's:installedCommands',
  [],
  {
    ...jsonStorage,
    getItem: (key, initialValue) => {
      const stored = jsonStorage.getItem(key, initialValue)
      return Array.isArray(stored) ? stored.map(normalizeInstalledSlashCommand) : initialValue
    }
  },
  { getOnInit: true }
)

function slashCommandKey(pubkey: string, d: string) {
  return `${pubkey}:${d}`
}

function getInstalledSlashCommands() {
  return getDefaultStore().get(installedSlashCommandsAtom)
}

export function isSlashCommandInstalled(
  installed: TInstalledSlashCommand[],
  pubkey: string,
  d: string
): boolean {
  const key = slashCommandKey(pubkey, d)
  return installed.some((c) => slashCommandKey(c.pubkey, c.d) === key)
}

/** Install (or replace) a command from its kind 31992 event. */
export function installSlashCommand(event: {
  pubkey: string
  tags: string[][]
  created_at: number
}) {
  const slashCommand = parseInstalledSlashCommand(event)
  if (!slashCommand) return

  getDefaultStore().set(installedSlashCommandsAtom, (prev) => {
    const key = slashCommandKey(event.pubkey, slashCommand.d)
    return [...prev.filter((c) => slashCommandKey(c.pubkey, c.d) !== key), slashCommand]
  })
}

/** Replace every installed command (used to mirror the user's kind 10992 list). */
export function replaceInstalledSlashCommands(slashCommands: TInstalledSlashCommand[]) {
  getDefaultStore().set(installedSlashCommandsAtom, slashCommands)
}

/** Parse a kind 31992 event into an installed command, if it has a trigger. */
export function parseInstalledSlashCommand(event: {
  pubkey: string
  tags: string[][]
  created_at: number
}): TInstalledSlashCommand | undefined {
  const definition = parseSlashCommandDefinition(event.tags)
  if (!definition.command) return undefined
  return {
    ...definition,
    command: definition.command,
    pubkey: event.pubkey,
    created_at: event.created_at
  }
}

/** If this is a newer version of an installed command, update the installed copy. */
export function refreshInstalledSlashCommand(event: {
  pubkey: string
  tags: string[][]
  created_at: number
}) {
  const definition = parseSlashCommandDefinition(event.tags)
  const installed = findInstalledSlashCommand(event.pubkey, definition.d)
  if (installed && installed.created_at < event.created_at) installSlashCommand(event)
}

export function uninstallSlashCommand(pubkey: string, d: string) {
  const key = slashCommandKey(pubkey, d)
  getDefaultStore().set(installedSlashCommandsAtom, (prev) =>
    prev.filter((c) => slashCommandKey(c.pubkey, c.d) !== key)
  )
}

/**
 * Search installed commands by trigger, title or description. `predicate` is
 * applied before `limit` so commands out of scope don't take up slots.
 */
export function findInstalledSlashCommands(
  query: string,
  limit = 20,
  predicate: (slashCommand: TInstalledSlashCommand) => boolean = () => true
): TInstalledSlashCommand[] {
  const q = query.toLowerCase()
  return getInstalledSlashCommands()
    .filter(
      (c) =>
        !q ||
        c.command.toLowerCase().includes(q) ||
        (c.title ?? '').toLowerCase().includes(q) ||
        (c.description ?? '').toLowerCase().includes(q)
    )
    .filter(predicate)
    .sort((a, b) => {
      // commands whose trigger starts with the query come first
      const aPrefix = a.command.toLowerCase().startsWith(q) ? 0 : 1
      const bPrefix = b.command.toLowerCase().startsWith(q) ? 0 : 1
      return aPrefix - bPrefix || a.command.localeCompare(b.command)
    })
    .slice(0, limit)
}

export function findInstalledSlashCommand(
  pubkey: string,
  d: string
): TInstalledSlashCommand | undefined {
  const key = slashCommandKey(pubkey, d)
  return getInstalledSlashCommands().find((c) => slashCommandKey(c.pubkey, c.d) === key)
}

/** Installed commands with the given trigger, optionally from a specific executor. */
export function findInstalledSlashCommandsByName(
  name: string,
  pubkey?: string
): TInstalledSlashCommand[] {
  return getInstalledSlashCommands().filter(
    (c) => c.command === name && (!pubkey || c.pubkey === pubkey)
  )
}
